import type { UseCaseTemplate } from '../use-case-template-model'

/**
 * High-intent external-agent workflows. One typed entry generates the listing,
 * detail page, sitemap URL, LLM-index entry, and structured how-to data.
 */
export const agentWorkflowUseCases: UseCaseTemplate[] = [
  {
    slug: 'google-ads-audit-with-claude-code',
    title: 'Audit Google Ads With Claude Code and Soku CLI',
    description:
      'Give Claude Code read-only access to Google Ads through Soku CLI, inspect tracking, search terms, creative coverage, budgets, and Quality Score, then save an evidence-backed account audit.',
    prompt:
      'Audit my Google Ads account for the last 30 days. Check tracking integrity, wasted search-term spend, Quality Score, creative coverage, budget allocation, device and geo performance. Return a prioritized scorecard with evidence and do not change the account.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'Google Ads', 'PPC'],
    integrations: ['Google Ads', 'GA4'],
    iconEmoji: '🔎',
    agentWorkflow: {
      outcome:
        'Claude Code produces a saved Google Ads audit with a severity-ranked scorecard, supporting metrics, and a separate action plan while leaving every campaign and setting unchanged.',
      bestFor:
        'PPC managers, founders, and agencies that already use Claude Code and want a repeatable account review without exporting reports by hand.',
      prerequisites: [
        'A Soku workspace with a connected Google Ads account',
        'Node.js 20 or newer and a shell available to Claude Code',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, install or update Soku CLI, sign in, and select my Soku workspace. Install the soku audit skills you need, then run a read-only Google Ads audit for the last 30 days. Save a scorecard with evidence, risks, and prioritized recommendations.',
      steps: [
        {
          title: 'Connect Claude Code to Soku',
          body: 'Let the agent follow the maintained split-flow login guide and select the remote workspace instead of inferring it from the local folder.',
          command:
            'npm i -g @soku-ai/cli && SOKU_NO_KEYCHAIN=1 soku auth login --no-wait',
        },
        {
          title: 'Discover the Google Ads account',
          body: 'List the accounts available in the selected workspace and confirm the intended customer before querying metrics.',
          command: 'soku ads list-ad-accounts --platform google',
        },
        {
          title: 'Run the audit playbooks',
          body: 'Install only the audit skills needed for tracking, search terms, Quality Score, and creative, then execute them against the agreed window.',
          command:
            'soku skill install audit-tracking audit-search-terms audit-quality-score audit-creative --global',
        },
        {
          title: 'Save the evidence',
          body: 'Ask Claude Code to write the scorecard and supporting tables to the current project without changing the connected ad account.',
        },
      ],
      expectedOutputs: [
        'A severity-ranked Google Ads audit scorecard with metric evidence',
        'A prioritized remediation plan separated from any account mutation',
      ],
      accessMode: 'read-only',
      safety:
        'This workflow is read-only. The agent may query ads and analytics data, but it must not call write actions or approve reviews. Any later remediation should be proposed separately for human review.',
      skillSlugs: [
        'audit-tracking',
        'audit-search-terms',
        'audit-quality-score',
        'audit-creative',
      ],
    },
  },
  {
    slug: 'meta-ads-review-with-codex',
    title: 'Review Meta Ads Performance With Codex and Soku CLI',
    description:
      'Connect Codex to Meta Ads reporting through Soku CLI, compare campaign and creative performance, identify fatigue and CPA changes, and return a read-only optimization brief with evidence.',
    prompt:
      'Review my Meta Ads performance for the last 14 days versus the previous 14 days. Find CPA and ROAS changes, fatigued creatives, delivery concentration, and budget anomalies. Explain each finding with data and do not edit the account.',
    category: 'Agent Workflows',
    tags: ['Codex', 'Meta Ads', 'Performance Marketing'],
    integrations: ['Meta Ads'],
    iconEmoji: '📊',
    agentWorkflow: {
      outcome:
        'Codex returns a period-over-period Meta Ads performance brief with campaign anomalies, creative-fatigue evidence, and recommended decisions that remain proposals until a human chooses to act.',
      bestFor:
        'Performance marketers who work in Codex and need a fast, reproducible Meta Ads review grounded in account data instead of screenshots.',
      prerequisites: [
        'A Soku workspace with a connected Meta Ads account',
        'Codex running in a shell with Node.js 20 or newer',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md and connect Codex to my Soku workspace. Use the Meta Ads reporting commands and the ads-report and audit-creative skills to compare the last 14 days with the previous 14 days. Produce a read-only brief covering CPA, ROAS, delivery, creative fatigue, and budget anomalies.',
      steps: [
        {
          title: 'Install and authenticate',
          body: 'Use the agent-safe device flow, then resume after the user approves in the browser.',
          command:
            'npm i -g @soku-ai/cli && SOKU_NO_KEYCHAIN=1 soku auth login --no-wait',
        },
        {
          title: 'Confirm the Meta account',
          body: 'Discover accessible Meta accounts and have the user resolve ambiguity before analysis.',
          command: 'soku ads list-ad-accounts --platform meta',
        },
        {
          title: 'Compare performance windows',
          body: 'Query campaign and creative dimensions for both periods and apply the same decision rules to each row.',
          command: 'soku skill install ads-report audit-creative --global',
        },
        {
          title: 'Deliver the brief',
          body: 'Separate observed facts from recommendations and preserve the queried date windows in the report.',
        },
      ],
      expectedOutputs: [
        'A two-period Meta Ads performance comparison with cited metrics',
        'A creative-fatigue and budget-anomaly decision list without account changes',
      ],
      accessMode: 'read-only',
      safety:
        'The review uses reporting data only. Codex must not call mutations or approve pending reviews; any recommended pause, budget move, or creative replacement remains a human decision.',
      skillSlugs: ['ads-report', 'audit-creative'],
    },
  },
  {
    slug: 'weekly-ads-report-with-ai-agent',
    title: 'Build a Weekly Cross-Channel Ads Report With an AI Agent',
    description:
      'Use a local AI agent and Soku CLI to combine Google Ads, Meta Ads, and GA4 into one weekly report that explains changes, flags anomalies, and preserves the reporting window.',
    prompt:
      "Create this week's cross-channel performance report for Google Ads, Meta Ads, and GA4. Compare with last week, explain material changes, list tracking caveats, and save a client-ready summary. Keep the workflow read-only.",
    category: 'Agent Workflows',
    tags: ['Claude Code', 'Codex', 'Reporting'],
    integrations: ['Google Ads', 'Meta Ads', 'GA4'],
    iconEmoji: '🗓️',
    agentWorkflow: {
      outcome:
        'The agent creates one client-ready weekly report with aligned date windows, cross-channel KPIs, material-change explanations, and explicit data-quality caveats.',
      bestFor:
        'Agencies and in-house teams that spend hours exporting separate platform reports and want a repeatable shell-based reporting workflow.',
      prerequisites: [
        'Google Ads or Meta Ads and GA4 connected to the same Soku brand',
        'A local shell agent with permission to save the finished report',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect to my Soku workspace, and install the soku ads-report skill. Build a read-only weekly report for Google Ads, Meta Ads, and GA4 using the same seven-day window and the previous seven days as comparison. Explain material changes, disclose missing data, and save both an executive summary and metric appendix.',
      steps: [
        {
          title: 'Select the reporting workspace',
          body: 'Resolve the brand explicitly so accounts from another client cannot enter the report.',
          command: 'soku workspace status && soku workspace resolve <brand>',
        },
        {
          title: 'Install the reporting playbook',
          body: 'Install the focused report skill rather than loading every available business skill.',
          command: 'soku skill install ads-report --global',
        },
        {
          title: 'Pull aligned windows',
          body: 'Use ads and GA4 commands with identical calendar boundaries and record the source of every metric.',
          command: 'soku ads --help && soku ga4 --help',
        },
        {
          title: 'Write the deliverables',
          body: 'Create an executive narrative and a metric appendix with caveats and next questions.',
        },
      ],
      expectedOutputs: [
        'A client-ready weekly cross-channel narrative',
        'A metric appendix with dates, sources, comparisons, and data caveats',
      ],
      accessMode: 'read-only',
      safety:
        'Reporting commands are read-only. The agent must keep client workspaces isolated, disclose missing sources, and avoid turning recommendations into account changes during the reporting run.',
      skillSlugs: ['ads-report'],
    },
  },
  {
    slug: 'ga4-landing-page-analysis-with-claude-code',
    title: 'Analyze GA4 Landing Pages With Claude Code and Soku CLI',
    description:
      'Let Claude Code query GA4 through Soku CLI, rank landing pages by traffic and conversion behavior, find paid-traffic leaks, and save an evidence-backed CRO shortlist.',
    prompt:
      'Analyze our GA4 landing pages for the last 30 days. Separate paid and organic traffic, identify high-traffic pages with weak engagement or conversion, and return the five best CRO opportunities with evidence. Do not alter analytics settings.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'GA4', 'CRO'],
    integrations: ['GA4'],
    iconEmoji: '📈',
    agentWorkflow: {
      outcome:
        'Claude Code produces a ranked CRO shortlist connecting landing-page traffic, engagement, conversion behavior, and acquisition source without changing GA4 configuration.',
      bestFor:
        'Growth and CRO teams that want a reproducible GA4 investigation in the same workspace where they write briefs, experiments, and landing-page code.',
      prerequisites: [
        'A GA4 property granted to the selected Soku workspace',
        'Known primary conversion events or a willingness to label missing conversion mapping',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect Claude Code to my Soku workspace, and use the GA4 commands for a read-only landing-page analysis covering the last 30 days. Segment paid and organic acquisition, rank high-traffic pages with weak engagement or conversion, and save five CRO opportunities with the exact metrics behind each one.',
      steps: [
        {
          title: 'Discover the GA4 property',
          body: 'List properties and confirm the correct one before querying any report.',
          command: 'soku ga4 list-properties',
        },
        {
          title: 'Pull the landing-page baseline',
          body: 'Retrieve top pages and a custom report that includes source and conversion metrics for the same date range.',
          command:
            'soku ga4 list-top-pages --property-id <property_id> --date-start <start> --date-end <end> --limit 50',
        },
        {
          title: 'Rank opportunities',
          body: 'Prioritize pages with enough traffic to matter and explain when conversion mapping is incomplete.',
        },
        {
          title: 'Save the CRO shortlist',
          body: 'Write one evidence block and one proposed experiment for each selected page.',
        },
      ],
      expectedOutputs: [
        'A source-segmented landing-page performance table',
        'Five prioritized CRO opportunities with evidence and experiment ideas',
      ],
      accessMode: 'read-only',
      safety:
        'This workflow only reads GA4 reports. It must not edit events, audiences, attribution, or property settings, and it must label conclusions as uncertain when conversion mapping is missing.',
      skillSlugs: ['ga4', 'landing-page-spy'],
    },
  },
  {
    slug: 'posthog-funnel-analysis-with-codex',
    title: 'Analyze a PostHog Funnel With Codex and Soku CLI',
    description:
      'Give Codex allowlisted, read-only PostHog access through Soku CLI, inspect the live project schema, measure a conversion funnel, and produce a drop-off diagnosis without exposing provider keys.',
    prompt:
      'Analyze the signup-to-activation funnel in PostHog for the last 30 days. Discover the real event schema first, quantify each step and segment, identify the largest evidence-backed drop-offs, and do not write to PostHog.',
    category: 'Agent Workflows',
    tags: ['Codex', 'PostHog', 'Product Analytics'],
    integrations: ['PostHog'],
    iconEmoji: '🧪',
    agentWorkflow: {
      outcome:
        'Codex returns a funnel table, segment comparison, and evidence-backed drop-off hypotheses after discovering the live PostHog schema instead of guessing event names.',
      bestFor:
        'Product-led growth teams that want Codex to analyze activation data while keeping PostHog credentials server-side and the command surface read-only.',
      prerequisites: [
        'A PostHog project granted to the active Soku brand',
        'A defined business question for the funnel window and activation event',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect Codex to my Soku workspace, and perform a read-only PostHog funnel analysis for signup to activation over the last 30 days. List projects, inspect the allowlisted tools and live data schema before querying, then quantify each funnel step and the most relevant segments. Use only event names found in the live schema.',
      steps: [
        {
          title: 'Select the PostHog project',
          body: 'List only the projects granted to the active Soku brand and confirm the intended project.',
          command: 'soku posthog list-projects',
        },
        {
          title: 'Inspect the live schema',
          body: 'Discover allowlisted read tools and event definitions before composing a query.',
          command: 'soku posthog list-tools --project-id <project_id>',
        },
        {
          title: 'Run the funnel query',
          body: 'Use the discovered schema and preserve the exact query in the report for reproducibility.',
          command:
            "soku posthog query --project-id <project_id> --tool execute-sql --arguments '<json>'",
        },
        {
          title: 'Explain drop-offs',
          body: 'Separate measured drop-offs from hypotheses that require qualitative or implementation evidence.',
        },
      ],
      expectedOutputs: [
        'A reproducible funnel table with the exact date window and query',
        'Segment-level drop-off findings separated from unverified hypotheses',
      ],
      accessMode: 'read-only',
      safety:
        'Soku exposes only allowlisted PostHog read tools here. The agent must inspect the schema, avoid guessed identifiers, keep credentials server-side, and make no PostHog mutations.',
      skillSlugs: ['posthog-growth-analytics'],
    },
  },
  {
    slug: 'search-console-quick-wins-with-ai-agent',
    title: 'Find Search Console Quick Wins With an AI Coding Agent',
    description:
      'Connect a shell-based AI agent to Google Search Console through Soku CLI, identify high-impression queries and pages near page one, and turn the evidence into a prioritized content refresh queue.',
    prompt:
      'Use Search Console data from the last 90 days to find high-impression queries and pages ranking in positions 4-20. Separate CTR problems from ranking problems and return a prioritized refresh queue without changing the site.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'Codex', 'SEO'],
    integrations: ['Google Search Console'],
    iconEmoji: '🔍',
    agentWorkflow: {
      outcome:
        'The agent creates a prioritized SEO refresh queue that distinguishes CTR opportunities from ranking opportunities and retains the Search Console evidence for every recommendation.',
      bestFor:
        'SEO and content teams that want their coding agent to ground refresh briefs in first-party Search Console data before editing any page.',
      prerequisites: [
        'Google Search Console access connected to the selected Soku brand',
        'A 90-day analysis window that excludes incomplete recent reporting days',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect my AI coding agent to Soku, and use the Search Console commands for a read-only 90-day quick-win analysis. Find high-impression queries and pages in positions 4-20, separate weak CTR from weak ranking, and save a prioritized refresh queue with the evidence for every item.',
      steps: [
        {
          title: 'Inspect the Search Console command surface',
          body: 'Read current command help instead of assuming flags or site identifiers.',
          command: 'soku gsc --help',
        },
        {
          title: 'Pull query and page evidence',
          body: 'Use the same complete reporting window for query-level and page-level comparisons.',
        },
        {
          title: 'Classify opportunities',
          body: 'Separate CTR, ranking, cannibalization, and declining-page cases so each receives the right intervention.',
        },
        {
          title: 'Create refresh briefs',
          body: 'Save a ranked queue with target query, current page, metrics, recommended change, and validation measure.',
          command: 'soku skill install gsc site-audit --global',
        },
      ],
      expectedOutputs: [
        'A ranked Search Console quick-win queue with query and page metrics',
        'A refresh brief for each selected page with a measurable validation target',
      ],
      accessMode: 'read-only',
      safety:
        'The analysis is read-only and must not edit or publish pages. Search Console data can lag, so the agent must exclude incomplete days and retain the exact reporting window in every brief.',
      skillSlugs: ['gsc', 'site-audit'],
    },
  },
  {
    slug: 'competitor-research-with-soku-egress',
    title: 'Run Competitor Research From Claude or Codex With Soku Egress',
    description:
      'Let Claude Code or Codex call covered research APIs through Soku egress, keep provider credentials server-side, and combine keyword, backlink, and landing-page evidence into a sourced competitor brief.',
    prompt:
      'Research our top five competitors across organic keywords, paid search, backlinks, positioning, and landing pages. Use covered APIs through Soku egress, cite every source, and separate observed facts from inference.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'Codex', 'Competitive Intelligence'],
    integrations: ['Ahrefs', 'DataForSEO'],
    iconEmoji: '🕵️',
    agentWorkflow: {
      outcome:
        'The local agent produces a sourced competitor brief from covered APIs without asking the user to paste Ahrefs, DataForSEO, or other provider credentials into the shell.',
      bestFor:
        'Strategy, SEO, and growth teams that want a coding agent to combine multiple research providers while keeping credential handling inside Soku.',
      prerequisites: [
        'A Soku workspace with access to the required covered egress providers',
        'A confirmed competitor set or permission for the agent to discover candidates',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect to my Soku workspace, list the available egress providers, and research our top five competitors across organic keywords, paid search, backlinks, positioning, and landing pages. Route covered API calls through soku egress, cite the source and date for every material fact, and clearly label inference. Never ask me to paste provider API keys.',
      steps: [
        {
          title: 'Discover covered providers',
          body: 'Confirm which research APIs Soku can route before planning the evidence collection.',
          command: 'soku egress providers',
        },
        {
          title: 'Confirm competitors and questions',
          body: 'Resolve the target set and define which observations will answer the business decision.',
          command:
            'soku skill install find-competitors google-ads-competitive-analysis --global',
        },
        {
          title: 'Collect sourced evidence',
          body: 'Prefix covered provider requests with Soku egress so credentials are injected on the server.',
          command:
            "soku egress -- curl 'https://api.ahrefs.com/v3/site-explorer/domain-rating?target=example.com&date=2026-09-29'",
        },
        {
          title: 'Synthesize the brief',
          body: 'Preserve URLs, retrieval dates, and uncertainty instead of presenting modeled estimates as first-party facts.',
        },
      ],
      expectedOutputs: [
        'A competitor evidence table with source URLs and retrieval dates',
        'A strategic brief that clearly separates observed facts, estimates, and inference',
      ],
      accessMode: 'read-only',
      safety:
        'Provider credentials stay on Soku servers and must never appear in prompts, command arguments, logs, or artifacts. The agent should treat third-party estimates as directional rather than audited facts.',
      skillSlugs: ['find-competitors', 'google-ads-competitive-analysis'],
    },
  },
  {
    slug: 'publish-seo-pages-with-soku-cli',
    title: 'Publish Researched SEO Pages With Soku CLI and Human Review',
    description:
      'Use an external coding agent to author complete SEO pages, inspect the rendered files, and publish through Soku CLI only after a human confirms the content, claims, and destination.',
    prompt:
      'Research and draft a complete SEO landing page for the approved keyword brief, save the HTML locally, show me the final title, claims, sources, and destination, then wait for my approval before publishing through Soku SEO Hosting.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'SEO Hosting', 'Publishing'],
    integrations: ['Google Search Console', 'Ahrefs'],
    iconEmoji: '🚀',
    agentWorkflow: {
      outcome:
        'The agent creates a complete, source-backed HTML page, exposes the exact publish destination and content for review, and only then sends the approved file to Soku SEO Hosting.',
      bestFor:
        'SEO teams using Claude Code, Codex, or Cursor to author owned-media pages while retaining a human checkpoint before anything becomes public.',
      prerequisites: [
        'SEO Hosting enabled for the selected Soku brand and destination section',
        'An approved keyword brief with sources, search intent, and page owner',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md and connect to my Soku workspace. Research and author a complete HTML page for the approved brief, validate links and claims, and save it locally. Show me the title, target URL, sources, and a content summary. Do not publish until I explicitly approve the reviewed file and destination; after approval, publish through Soku SEO Hosting and verify the live URL.',
      steps: [
        {
          title: 'Confirm hosting and destination',
          body: 'Inspect the active brand, connection, and section before authoring so the agent cannot publish to an unintended site.',
          command: 'soku seo-hosting status',
        },
        {
          title: 'Author and validate locally',
          body: 'Create a complete HTML document, validate claims and links, and keep the first version as a local artifact.',
          command:
            'soku skill install seo-hosting landing-page-create --global',
        },
        {
          title: 'Request human approval',
          body: 'Present the final file, target section, slug, title, and sources; stop until the user approves that exact revision.',
        },
        {
          title: 'Publish and verify',
          body: 'Upload the approved file, publish it, and verify that the expected live URL returns the reviewed content.',
          command:
            "soku seo-hosting pages put --section <section> --slug <slug> --title '<title>' --html-file page.html",
        },
      ],
      expectedOutputs: [
        'A locally reviewable complete HTML document with source notes',
        'An approved and verified SEO Hosting URL matching the reviewed revision',
      ],
      accessMode: 'review-gated-write',
      safety:
        'Publishing changes a public site. The agent must show the exact file and destination, receive explicit human approval for that revision, avoid credentials in argv, and verify the resulting URL after publishing.',
      skillSlugs: ['seo-hosting', 'landing-page-create'],
    },
  },
  {
    slug: 'install-soku-marketing-skill-in-cursor',
    title: 'Install a Soku Marketing Skill in Cursor With the CLI',
    description:
      'Use Soku CLI to discover and install one focused marketing playbook into Cursor, verify the local skill files, and invoke it for a concrete task without loading the entire catalog.',
    prompt:
      'Connect Cursor to my Soku workspace, find the best Soku skill for a Google Ads search-terms audit, install only that skill globally, verify the installation, and use it to produce a read-only audit brief.',
    category: 'Agent Workflows',
    tags: ['Cursor', 'Agent Skills', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🧩',
    agentWorkflow: {
      outcome:
        'Cursor receives one relevant Soku playbook, verifies its managed installation, and immediately applies it to a concrete read-only marketing task instead of stopping after setup.',
      bestFor:
        'Cursor users who want focused Soku playbooks in skill autocomplete without installing every business skill or manually copying SKILL.md files.',
      prerequisites: [
        'Cursor or another client with a supported local agent skill directory',
        'Soku CLI installed and authenticated to the intended workspace',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect Cursor to my Soku workspace, search the Soku skill catalog for the focused playbook needed to audit Google Ads search terms, install only that business skill globally, and verify the managed installation. Then use it for a read-only 30-day audit and save the brief.',
      steps: [
        {
          title: 'Inspect the catalog',
          body: 'List available business skills and select the narrowest playbook that matches the requested task.',
          command: 'soku skill list',
        },
        {
          title: 'Install one focused skill',
          body: 'Install the selected Soku-managed skill into the global client skill directory.',
          command: 'soku skill install audit-search-terms --global',
        },
        {
          title: 'Verify managed state',
          body: 'Check the install manifest and resolved skill path before invoking it.',
          command:
            'soku skill status --global && soku skill list-installed --global',
        },
        {
          title: 'Run the first task',
          body: 'Invoke the installed skill for the agreed account and window, producing an artifact rather than treating installation as activation.',
        },
      ],
      expectedOutputs: [
        'A verified Soku-managed skill installation visible to Cursor',
        'A first read-only audit artifact produced with the installed playbook',
      ],
      accessMode: 'read-only',
      safety:
        'Install only the requested managed skill, preserve unrelated local skills, and keep the first task read-only. The agent must not remove user files or mutate the connected account.',
      skillSlugs: ['audit-search-terms'],
    },
  },
  {
    slug: 'review-gated-ad-changes-with-ai-agent',
    title: 'Make Review-Gated Ad Changes From an External AI Agent',
    description:
      'Let Claude Code or Codex prepare an ads mutation through Soku CLI, hand you a link to the exact pending review, and execute only after you approve that specific change on the Soku approval page.',
    prompt:
      'Prepare a conversion group named Qualified Leads from the agreed conversion actions. Show me the exact change summary and pending review first. Do not approve or execute anything until I explicitly authorize that review.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'Codex', 'Human in the Loop'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🛡️',
    agentWorkflow: {
      outcome:
        'The external agent prepares a specific ads change, hands over the review link and human-readable diff, and the change runs only after a human approves that exact review on the Soku approval page.',
      bestFor:
        'Ad operators who want external agents to prepare deterministic account changes without granting unattended authority over live spend or conversion configuration.',
      prerequisites: [
        'The intended ad account and conversion actions confirmed in the active Soku workspace',
        'A browser signed in to Soku, where the human opens the approval link the agent hands over',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect to my Soku workspace, and prepare the requested conversion group with soku call using a precise summary. Show the returned review ID, parameters, account, and proposed effect, and give me its approve_url. Never run the approval command yourself: wait with soku review wait until I approve or deny it on that page, then verify the result once.',
      steps: [
        {
          title: 'Confirm the target account and inputs',
          body: 'Resolve ambiguous accounts and conversion actions before creating any pending review.',
          command: 'soku workspace status',
        },
        {
          title: 'Create a pending review',
          body: 'Submit the intended mutation with a concise summary so the review is understandable outside the agent transcript.',
          command:
            "soku call ads create_conversion_group --summary 'create group: qualified leads' -p name='Qualified Leads'",
        },
        {
          title: 'Hand over the approval link',
          body: 'Show the exact pending review and give the human its approve_url. They approve or deny it on the signed-in Soku approval page, not in the agent\'s terminal.',
          command: 'soku review show <review_id>',
        },
        {
          title: 'Wait for the decision and verify',
          body: 'Wait for the human to decide instead of approving, then read back the result once without replaying the write. A denial or a failed write exits non-zero.',
          command: 'soku review wait <review_id>',
        },
      ],
      expectedOutputs: [
        'A pending review with the exact account, parameters, summary, and review ID',
        'A single verified change only after explicit human authorization',
      ],
      accessMode: 'review-gated-write',
      safety:
        'The pending review is not permission to execute. A human approves the exact review on the Soku approval page; the agent never runs soku review approve itself, and approval must never be allowlisted, replayed, or generalized to later writes.',
      skillSlugs: ['google-ads', 'meta-ads'],
    },
  },
  {
    slug: 'brand-memory-marketing-analysis-with-ai-agent',
    title: 'Ground an AI Agent in Brand Memory Before Marketing Analysis',
    description:
      'Let an external AI agent read Soku brand memory before analyzing campaigns, then verify every performance claim against current ads or analytics data instead of treating memory as live truth.',
    prompt:
      'Read our brand positioning, KPI targets, audience definitions, and reporting preferences from Soku memory before analyzing performance. Verify every current metric with live data and label any memory that appears stale.',
    category: 'Agent Workflows',
    tags: ['Claude Code', 'Codex', 'Brand Context'],
    integrations: ['Soku Memory', 'Google Ads', 'Meta Ads'],
    iconEmoji: '🧠',
    agentWorkflow: {
      outcome:
        'The agent uses durable brand context to frame the analysis while grounding every time-sensitive performance conclusion in fresh ads or analytics data and flagging stale memory.',
      bestFor:
        'Teams that want consistent brand-aware analysis across external agents without pasting positioning, KPI targets, and reporting preferences into every prompt.',
      prerequisites: [
        'Relevant positioning, KPI, audience, or reporting references stored in Soku memory',
        'At least one live data source available to verify time-sensitive claims',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md, connect to my Soku workspace, and inspect brand memory for positioning, KPI targets, audiences, and reporting preferences before the analysis. Treat memory as background context, not live measurement. Verify every current performance claim with ads or analytics commands, cite the source window, and flag memory that conflicts with fresh data. Keep the workflow read-only.',
      steps: [
        {
          title: 'Discover relevant memory',
          body: 'Search for only the references needed by the task instead of loading unrelated brand history.',
          command:
            "soku memory search 'positioning KPI audience reporting preferences'",
        },
        {
          title: 'Read the selected references',
          body: 'Retrieve the named memory records and retain their type and age as provenance.',
          command: 'soku memory list',
        },
        {
          title: 'Verify live claims',
          body: 'Use current ads or analytics commands for every metric and compare them with targets from memory.',
          command: 'soku ads --help && soku ga4 --help',
        },
        {
          title: 'Report context conflicts',
          body: 'Call out stale or conflicting memory explicitly rather than silently choosing one source.',
        },
      ],
      expectedOutputs: [
        'A brand-aware analysis with memory references and current data citations',
        'A list of stale or conflicting memory items that need human correction',
      ],
      accessMode: 'read-only',
      safety:
        'Memory is contextual and may be stale. The agent must verify time-sensitive facts against live data, expose conflicts, avoid treating preferences as measured outcomes, and make no account changes.',
      skillSlugs: ['ads-report'],
    },
  },
  {
    slug: 'agency-account-triage-with-soku-cli',
    title: 'Triage Multiple Client Ad Accounts With Soku CLI',
    description:
      'Use a shell-based AI agent to scan agency client workspaces one at a time, identify accounts needing attention, and produce a prioritized triage queue without mixing client data or changing campaigns.',
    prompt:
      "Triage this week's client ad accounts for CPA spikes, pacing risk, tracking failures, and creative fatigue. Keep every client workspace isolated, cite the account and date window, and return a prioritized queue without making changes.",
    category: 'Agent Workflows',
    tags: ['Agency', 'Claude Code', 'Multi-Account'],
    integrations: ['Google Ads', 'Meta Ads', 'GA4'],
    iconEmoji: '🚨',
    agentWorkflow: {
      outcome:
        'The agent returns an agency-wide triage queue with evidence per client while maintaining explicit workspace boundaries and leaving all connected accounts unchanged.',
      bestFor:
        'Agencies that manage many brands and need a repeatable first-pass review before media buyers spend time opening every advertising dashboard.',
      prerequisites: [
        'Each client represented by the correct Soku org and brand workspace',
        'A documented triage window and thresholds for CPA, pacing, tracking, and fatigue',
      ],
      agentPrompt:
        'Read https://soku.ai/cli/skill.md and connect to Soku. Triage the approved client brands one workspace at a time for CPA spikes, pacing risk, tracking failures, and creative fatigue over the agreed window. Confirm the active workspace before every client, keep artifacts separated by brand, cite account IDs and dates, and produce one prioritized agency queue.',
      steps: [
        {
          title: 'Select and verify each client workspace',
          body: 'Before every client, repeat the resolve → user-confirm → use-brand → status sequence, and only then read that client account data.',
          command:
            'soku workspace resolve <brand> && soku workspace use-brand <confirmed-brand> && soku workspace status',
        },
        {
          title: 'Run the same triage checks',
          body: "Apply the identical read-only thresholds and date window to each client's available accounts.",
          command:
            'soku skill install ads-report audit-tracking audit-creative --global',
        },
        {
          title: 'Persist client-isolated evidence',
          body: "Save each client's findings under a separate path with workspace and account identifiers.",
        },
        {
          title: 'Assemble the agency queue',
          body: 'Rank issues by severity and urgency only after every approved workspace has been completed.',
          command: 'soku ads --help',
        },
      ],
      expectedOutputs: [
        'A separate evidence-backed triage summary for every approved client brand',
        'An agency-wide priority queue with severity, owner, account, and date window',
      ],
      accessMode: 'read-only',
      safety:
        'The agent must confirm workspace status before every client, keep artifacts and identifiers isolated, skip ambiguous brands, and perform no campaign mutations during triage.',
      skillSlugs: ['ads-report', 'audit-tracking', 'audit-creative'],
    },
  },
]
